// Chi tiết một bảng trong thư viện: đơn vị đang dùng, gán cho cấp dưới trực tiếp, chuyển hàng
// loạt, lưu trữ, giá theo từng phiên bản (đang áp dụng / đã hẹn).

import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Archive, Edit2 } from 'lucide-react'
import { toast } from 'sonner'
import { get, post } from '@/lib/api'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { confirm } from '@/components/ui/confirm'
import { IndexBadge } from '@/components/ui/data-table'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { PriceListEditor, type PriceGrid } from '@/components/PriceListEditor'
import {
  KIND_LABEL,
  effectiveVersionNumber,
  supersededBy,
  errorDetails,
  errorMessage,
  fmtDateTime,
  type LibraryDetail,
  type LibraryRow,
  type NetworkUnit,
} from './types'

const SELECT_CLS = 'h-11 w-full min-w-0 flex-1 rounded-md border border-rule bg-raise px-2 text-sm text-ink desktop:h-9'

export function PriceListDetailDialog({
  id,
  grid,
  library,
  units,
  onClose,
  onEdit,
}: {
  id: string
  grid: PriceGrid | null
  /** Thư viện của tôi — để chọn bảng đích khi chuyển đơn vị. */
  library: LibraryRow[]
  /** Mạng lưới của tôi (/network/units) — để gán bảng cho cấp dưới trực tiếp. */
  units: NetworkUnit[]
  onClose: () => void
  onEdit: (row: LibraryRow) => void
}) {
  const qc = useQueryClient()
  const q = useQuery({
    queryKey: ['price-lists', 'library', id],
    queryFn: () => get<{ priceList: LibraryDetail }>(`/price-lists/library/${id}`),
  })
  const [picked, setPicked] = useState<number | null>(null)
  const [assignTo, setAssignTo] = useState('')
  const [moveTo, setMoveTo] = useState('')
  const [blockedBy, setBlockedBy] = useState<Array<{ id: string; name: string }> | null>(null)
  const d = q.data?.priceList
  const row = library.find((l) => l.id === id)

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['price-lists'] })
    qc.invalidateQueries({ queryKey: ['network-units'] })
  }

  const assign = useMutation({
    mutationFn: (unitShopId: string) => post('/price-lists/assign', { unitShopId, priceListId: id }),
    onSuccess: (_r, unitShopId) => {
      toast.success(`Đã gán bảng ${d?.code ?? ''} cho ${units.find((u) => u.id === unitShopId)?.name ?? 'đơn vị'}`)
      setAssignTo('')
      refresh()
    },
    onError: (e) => toast.error(errorMessage(e, 'Không gán được bảng giá')),
  })

  const move = useMutation({
    mutationFn: (toId: string) => post<{ moved: number }>(`/price-lists/library/${id}/move-units`, { toId }),
    onSuccess: (r, toId) => {
      toast.success(`Đã chuyển ${r.moved} đơn vị sang ${library.find((l) => l.id === toId)?.code ?? 'bảng mới'}`)
      setMoveTo('')
      setBlockedBy(null)
      refresh()
    },
    onError: (e) => toast.error(errorMessage(e, 'Không chuyển được đơn vị')),
  })

  const archive = useMutation({
    mutationFn: () => post(`/price-lists/library/${id}/archive`),
    onSuccess: () => {
      toast.success(`Đã lưu trữ bảng ${d?.code ?? ''}`)
      refresh()
      onClose()
    },
    onError: (e) => {
      const units = errorDetails<{ units?: Array<{ id: string; name: string }> }>(e, 'price_list.in_use')?.units
      if (units) setBlockedBy(units)
      else toast.error(errorMessage(e, 'Không lưu trữ được bảng giá'))
    },
  })

  const now = Date.now()
  const effective = d ? effectiveVersionNumber(d.versions, now) : null
  const shownNo = picked ?? effective ?? d?.versions[0]?.version ?? null
  const shown = d?.versions.find((v) => v.version === shownNo)
  const assignable = d ? units.filter((u) => u.direct && u.tenantType === d.kind && u.priceList?.code !== d.code) : []
  const moveTargets = d ? library.filter((l) => l.kind === d.kind && l.id !== d.id) : []

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-3xl">
        <DialogHeader className="pr-8">
          <DialogTitle>{d ? d.name : 'Bảng giá'}</DialogTitle>
          {d && (
            <DialogDescription>
              <span className="font-mono">{d.code}</span> · Bảng {KIND_LABEL[d.kind]} · {d.units.length} đơn vị đang dùng
            </DialogDescription>
          )}
        </DialogHeader>

        {!d ? (
          <p className="py-6 text-center text-sm text-muted">{q.isError ? 'Không tải được bảng giá.' : 'Đang tải…'}</p>
        ) : (
          <div className="space-y-6">
            <div className="flex flex-wrap gap-2">
              <Button size="sm" className="gap-1.5" disabled={!row} onClick={() => row && onEdit(row)}>
                <Edit2 className="size-4" /> Sửa giá
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="gap-1.5 text-danger hover:bg-danger/10"
                disabled={archive.isPending}
                onClick={async () => {
                  const ok = await confirm({
                    title: `Lưu trữ bảng ${d.code}?`,
                    description: 'Bảng đã lưu trữ không gán và không sửa được nữa.',
                    confirmLabel: 'Lưu trữ',
                    tone: 'danger',
                  })
                  if (ok) archive.mutate()
                }}
              >
                <Archive className="size-4" /> Lưu trữ
              </Button>
            </div>

            {blockedBy && (
              <div className="rounded-lg bg-danger/5 p-3 text-sm text-danger">
                <p className="font-medium">Chưa lưu trữ được: {blockedBy.length} đơn vị đang dùng bảng này.</p>
                <p className="mt-1 text-xs">{blockedBy.map((u) => u.name).join(', ')}</p>
              </div>
            )}

            <section className="space-y-3">
              <h3 className="text-sm font-medium text-ink">Đơn vị đang dùng ({d.units.length})</h3>
              {d.units.length === 0 ? (
                <p className="text-sm text-muted">Chưa đơn vị nào dùng bảng này.</p>
              ) : (
                <ul className="divide-y divide-rule rounded-lg border border-rule">
                  {d.units.map((u, i) => (
                    <li key={u.id} className="flex items-center justify-between gap-2 px-3 py-2 text-sm">
                      <span className="flex min-w-0 items-center gap-2">
                        <IndexBadge n={i + 1} />
                        <span className="min-w-0 truncate text-ink">{u.name}</span>
                      </span>
                      <span className="shrink-0 text-xs text-muted">
                        {KIND_LABEL[u.tenantType] ?? u.tenantType}
                        {u.status !== 'active' ? ` · ${u.status}` : ''}
                      </span>
                    </li>
                  ))}
                </ul>
              )}

              <div className="space-y-1.5">
                <p className="text-sm text-inkSoft">Gán cho {KIND_LABEL[d.kind]}</p>
                {assignable.length === 0 ? (
                  <p className="text-xs text-muted">Không còn {KIND_LABEL[d.kind]} trực thuộc nào dùng bảng khác.</p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    <select value={assignTo} onChange={(e) => setAssignTo(e.target.value)} className={SELECT_CLS} aria-label="Chọn đơn vị để gán">
                      <option value="">— Chọn {KIND_LABEL[d.kind]} —</option>
                      {assignable.map((u) => (
                        <option key={u.id} value={u.id}>
                          {u.name}
                          {u.priceList ? ` (đang dùng ${u.priceList.code})` : ' (chưa có bảng)'}
                        </option>
                      ))}
                    </select>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={!assignTo || assign.isPending}
                      onClick={async () => {
                        const u = assignable.find((x) => x.id === assignTo)
                        if (!u) return
                        const ok = await confirm({
                          title: `Gán ${d.code} cho ${u.name}?`,
                          description: 'Bảng mới thay bảng đang gán và áp dụng ngay cho đơn tạo sau đó.',
                          confirmLabel: 'Gán bảng',
                        })
                        if (ok) assign.mutate(u.id)
                      }}
                    >
                      {assign.isPending ? 'Đang gán…' : 'Gán'}
                    </Button>
                  </div>
                )}
              </div>

              {d.units.length > 0 && (
                <div className="space-y-1.5">
                  <p className="text-sm text-inkSoft">Chuyển cả {d.units.length} đơn vị sang bảng khác</p>
                  {moveTargets.length === 0 ? (
                    <p className="text-xs text-muted">Chưa có bảng {KIND_LABEL[d.kind]} nào khác trong thư viện.</p>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      <select value={moveTo} onChange={(e) => setMoveTo(e.target.value)} className={SELECT_CLS} aria-label="Chọn bảng đích">
                        <option value="">— Chọn bảng đích —</option>
                        {moveTargets.map((l) => (
                          <option key={l.id} value={l.id}>
                            {l.name} ({l.code})
                          </option>
                        ))}
                      </select>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={!moveTo || move.isPending}
                        onClick={async () => {
                          const to = moveTargets.find((l) => l.id === moveTo)
                          if (!to) return
                          const ok = await confirm({
                            title: `Chuyển ${d.units.length} đơn vị sang ${to.code}?`,
                            description: 'Bảng mới áp dụng ngay cho các đơn vị này.',
                            confirmLabel: 'Chuyển',
                          })
                          if (ok) move.mutate(to.id)
                        }}
                      >
                        {move.isPending ? 'Đang chuyển…' : 'Chuyển'}
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </section>

            <section className="space-y-2">
              <h3 className="text-sm font-medium text-ink">
                {shown ? (shown.version === effective ? `Giá đang áp dụng · v${shown.version}` : `Giá phiên bản v${shown.version}`) : 'Giá'}
              </h3>
              {shown && (shown.grid ?? d.grid ?? grid) ? (
                <PriceListEditor grid={(shown.grid ?? d.grid ?? grid)!} value={shown.data} readOnly />
              ) : null}
            </section>

            <section className="space-y-2">
              <h3 className="text-sm font-medium text-ink">Lịch sử phiên bản</h3>
              <ul className="divide-y divide-rule rounded-lg border border-rule">
                {d.versions.map((v) => {
                  const future = new Date(v.effectiveFrom).getTime() > now
                  // Bản hẹn đã bị bản sửa sau thay (vd. hẹn tăng rồi giảm áp ngay) — không còn "đã hẹn"
                  const replacedBy = future ? supersededBy(d.versions, v) : null
                  return (
                    <li key={v.version}>
                      <button
                        type="button"
                        onClick={() => setPicked(v.version)}
                        className={cn(
                          'motion-state flex min-h-11 w-full flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2 text-left text-sm hover:bg-paperSoft',
                          v.version === shownNo && 'bg-accent/5',
                        )}
                      >
                        <span className="font-mono text-xs font-semibold text-ink">v{v.version}</span>
                        {v.version === effective && <span className="rounded-full bg-ok/15 px-2 py-0.5 text-xs font-medium text-ok">Đang áp dụng</span>}
                        {future && !replacedBy && <span className="rounded-full bg-warn/15 px-2 py-0.5 text-xs font-medium text-warn">Đã hẹn</span>}
                        {replacedBy && (
                          <span className="rounded-full bg-paperSoft px-2 py-0.5 text-xs font-medium text-inkSoft">Không áp dụng — thay bằng v{replacedBy.version}</span>
                        )}
                        <span className="min-w-0 flex-1 text-xs text-inkSoft">
                          Từ {fmtDateTime(v.effectiveFrom)}
                          {v.changeNote ? ` · ${v.changeNote}` : ''}
                        </span>
                      </button>
                    </li>
                  )
                })}
              </ul>
            </section>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
